import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Zap,
  ShieldAlert,
  Wind,
  Activity,
  Code2,
} from "lucide-react";

import "./ExpertiseComponent.css";

/* ============================================================
   DATA
============================================================ */

const leftCards = [
  {
    icon: ShieldCheck,
    title: "Grid Compliance Study",
    desc: "Ensuring full regulatory compliance and grid code adherence for stable, reliable network operation.",
  },
  {
    icon: Zap,
    title: "Evacuation Study",
    desc: "Advanced transmission planning for efficient evacuation of renewable and conventional generation.",
  },
  {
    icon: ShieldAlert,
    title: "Protection Study",
    desc: "Precision relay coordination and fault analysis to safeguard critical power infrastructure.",
  },
];

const rightCards = [
  {
    icon: Wind,
    title: "Renewable Integration",
    desc: "Seamless integration of solar, wind and hybrid assets into modern, resilient power grids.",
  },
  {
    icon: Activity,
    title: "Power Quality",
    desc: "Voltage stability, harmonic mitigation and performance optimization across the network.",
  },
  {
    icon: Code2,
    title: "Software Development",
    desc: "Custom engineering software and digital tools built for the utilities of tomorrow.",
  },
];

/* ============================================================
   MOTION VARIANTS
============================================================ */

const headerVariant = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: "easeOut",
    },
  },
};

const cardVariant = {
  hidden: {
    opacity: 0,
    y: 34,
  },

  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

const hubVariant = {
  hidden: {
    opacity: 0,
    scale: 0.7,
  },

  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.9,
      ease: "easeOut",
    },
  },
};

/* ============================================================
   CARD
============================================================ */

function Card({ icon: Icon, title, desc, index }) {
  return (
    <motion.div
      className="expertise-card"
      custom={index}
      variants={cardVariant}
      initial="hidden"
      whileInView="visible"
      viewport={{
        once: true,
        amount: 0.4,
      }}
      whileHover={{ y: -8 }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 20,
      }}
    >
      <div className="card-icon">
        <Icon
          size={22}
          strokeWidth={1.8}
        />
      </div>

      <div className="card-body">
        <h3>{title}</h3>
        <p>{desc}</p>
      </div>

      <div className="card-edge-glow" />
    </motion.div>
  );
}

/* ============================================================
   NETWORK
============================================================ */

function EnergyNetwork() {
  return (
    <svg
      className="expertise-network"
      viewBox="0 0 1100 508"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>

        <filter
          id="softGlow"
          x="-60%"
          y="-60%"
          width="220%"
          height="220%"
        >
          <feGaussianBlur
            stdDeviation="5"
            result="blur"
          />

          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <filter
          id="bigGlow"
          x="-100%"
          y="-100%"
          width="300%"
          height="300%"
        >
          <feGaussianBlur
            stdDeviation="12"
            result="blur"
          />

          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <linearGradient
          id="mainStroke"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="0%"
        >
          <stop
            offset="0%"
            stopColor="#00D4FF"
          />

          <stop
            offset="55%"
            stopColor="#72E7FF"
          />

          <stop
            offset="100%"
            stopColor="#9EEFFF"
          />
        </linearGradient>

        <radialGradient
          id="hubGradient"
          cx="50%"
          cy="50%"
          r="50%"
        >
          <stop
            offset="0%"
            stopColor="rgba(0,212,255,.35)"
          />

          <stop
            offset="70%"
            stopColor="rgba(0,212,255,.08)"
          />

          <stop
            offset="100%"
            stopColor="rgba(0,212,255,0)"
          />
        </radialGradient>

      </defs>

      {/* ===================== AMBIENT BACKDROP ===================== */}

      <circle
        cx="550"
        cy="254"
        r="190"
        fill="url(#hubGradient)"
        className="hub-aura"
      />

      {/* ===================== MESH ===================== */}

      <g className="mesh-layer">

        <path d="M60 40 C260 120 340 200 470 254" />

        <path d="M60 468 C260 388 340 310 470 254" />

        <path d="M1040 40 C840 120 760 200 630 254" />

        <path d="M1040 468 C840 388 760 310 630 254" />

        <path d="M0 254 C220 200 340 150 470 254" />

        <path d="M1100 254 C880 200 760 150 630 254" />

      </g>

      {/* ===================== SECONDARY CABLES ===================== */}

      <path
        d="M400 78 C470 78 520 160 470 254"
        className="cable-thin"
      />

      <path
        d="M400 430 C470 430 520 348 470 254"
        className="cable-thin"
      />

      <path
        d="M700 78 C630 78 580 160 630 254"
        className="cable-thin"
      />

      <path
        d="M700 430 C630 430 580 348 630 254"
        className="cable-thin"
      />

      {/* ===================== PRIMARY GLOW ===================== */}

      <path
        d="M400 78 C460 78 470 190 470 254"
        className="line-underlay"
      />

      <path
        d="M400 254 L470 254"
        className="line-underlay"
      />

      <path
        d="M400 430 C460 430 470 318 470 254"
        className="line-underlay"
      />

      <path
        d="M700 78 C640 78 630 190 630 254"
        className="line-underlay"
      />

      <path
        d="M700 254 L630 254"
        className="line-underlay"
      />

      <path
        d="M700 430 C640 430 630 318 630 254"
        className="line-underlay"
      />

      {/* ===================== PRIMARY LINES ===================== */}

      <path
        id="p1"
        d="M400 78 C460 78 470 190 470 254"
        className="energy-line"
      />

      <path
        id="p2"
        d="M400 254 L470 254"
        className="energy-line"
      />

      <path
        id="p3"
        d="M400 430 C460 430 470 318 470 254"
        className="energy-line"
      />

      <path
        id="p4"
        d="M700 78 C640 78 630 190 630 254"
        className="energy-line"
      />

      <path
        id="p5"
        d="M700 254 L630 254"
        className="energy-line"
      />

      <path
        id="p6"
        d="M700 430 C640 430 630 318 630 254"
        className="energy-line"
      />

      {/* ===================== PARTICLES ===================== */}

      {["p1", "p2", "p3", "p4", "p5", "p6"].map(
        (id, i) => (
          <circle
            key={id}
            r="3.2"
            className="particle"
            filter="url(#softGlow)"
          >
            <animateMotion
              dur={`${2.6 + i * 0.35}s`}
              repeatCount="indefinite"
              begin={`${i * 0.4}s`}
            >
              <mpath href={`#${id}`} />
            </animateMotion>
          </circle>
        )
      )}

      {/* ===================== NODES ===================== */}

      <circle
        cx="400"
        cy="78"
        r="3.5"
        className="node-dot blink"
        style={{
          animationDelay: "0s",
        }}
      />

      <circle
        cx="400"
        cy="430"
        r="3.5"
        className="node-dot blink"
        style={{
          animationDelay: "1.1s",
        }}
      />

      <circle
        cx="700"
        cy="78"
        r="3.5"
        className="node-dot blink"
        style={{
          animationDelay: "0.6s",
        }}
      />

      <circle
        cx="700"
        cy="430"
        r="3.5"
        className="node-dot blink"
        style={{
          animationDelay: "1.7s",
        }}
      />

      <circle
        cx="470"
        cy="254"
        r="7"
        className="hub-node"
        filter="url(#softGlow)"
      />

      <circle
        cx="630"
        cy="254"
        r="7"
        className="hub-node"
        filter="url(#softGlow)"
      />

      {/* ===================== CENTRAL HUB ===================== */}

      <circle
        cx="550"
        cy="254"
        r="82"
        className="hub-ring-outer"
      />

      <circle
        cx="550"
        cy="254"
        r="60"
        className="hub-ring-inner"
      />

    </svg>
  );
}

/* ============================================================
   MAIN COMPONENT
============================================================ */

export default function ExpertiseComponent() {
  return (
    <section className="expertise-section">

      <div className="ambient-blob blob-a" />

      <div className="ambient-blob blob-b" />

      <div className="grid-overlay" />

      <div className="expertise-wrapper">

        <motion.div
          className="expertise-header"
          variants={headerVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.6,
          }}
        >

          <span className="eyebrow">
            OUR EXPERTISE
          </span>

          <h2>
            Engineering{" "}
            <span className="gradient-text">
              Excellence
            </span>
          </h2>

          <p>
            Delivering world-class engineering consultancy
            across power systems, renewable integration and
            digital infrastructure.
          </p>

        </motion.div>

        <div className="expertise-layout">

          <EnergyNetwork />

          <div className="expertise-column">

            {leftCards.map((item, index) => (
              <Card
                key={item.title}
                index={index}
                {...item}
              />
            ))}

          </div>

          <motion.div
            className="expertise-hub"
            variants={hubVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.6,
            }}
          >

            <div className="hub-breathe" />

            <div className="hub-ring ring-1" />

            <div className="hub-ring ring-2" />

            <div className="hub-ring ring-3" />

            <div className="hub-core"></div>

          </motion.div>

          <div className="expertise-column">

            {rightCards.map((item, index) => (
              <Card
                key={item.title}
                index={index + 3}
                {...item}
              />
            ))}

          </div>

        </div>

      </div>

    </section>
  );
}